import React__default, { useEffect, useRef, useState, createElement } from 'react';
import { debounce, range } from 'lodash';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useSpring, animated } from 'react-spring';
import { useMeasure, useUpdateEffect, usePrevious } from 'react-use';
import 'react-day-picker/lib/style.css';
import DayPicker, { DateUtils } from 'react-day-picker';
import { DateTime } from 'luxon';
import InputMask from 'react-input-mask';
import TimeKeeper from 'react-timekeeper';

var NavigationButton = function (props) {
    return (React__default.createElement("div", { className: "navigation-button-component " + (props.hideNavigationButtons ? 'text-only-button' : '') + " " + (props.className || ''), onClick: props.onClick },
        !props.hideNavigationButtons && (React__default.createElement("span", { className: props.disablePreviousButton ? 'previous-button disabled' : 'previous-button', onClick: props.onClickPrevious },
            React__default.createElement("i", { className: "icon-left-open" }))),
        React__default.createElement("span", { className: "button-text" },
            props.buttonText,
            props.children),
        !props.hideNavigationButtons && (React__default.createElement("span", { className: props.disablePreviousButton ? 'next-button disabled' : 'next-button', onClick: props.onClickNext },
            React__default.createElement("i", { className: "icon-right-open" })))));
};

var ButtonGroup = function (props) {
    return React__default.createElement("div", { className: "button-group-component" }, props.children);
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

// Hook
function useOnClickOutside(ref, handler) {
    useEffect(function () {
        var listener = function (event) {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return function () {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, 
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]);
}

var Button = function (_a) {
    var className = _a.className, primary = _a.primary, secondary = _a.secondary, success = _a.success, warning = _a.warning, danger = _a.danger, outline = _a.outline, textOnly = _a.textOnly, iconButton = _a.iconButton, switchButton = _a.switchButton, tooltipText = _a.tooltipText, children = _a.children, clickOutsideCallback = _a.clickOutsideCallback, preventDoubleClick = _a.preventDoubleClick, onClick = _a.onClick, loading = _a.loading, renderLoadingIcon = _a.renderLoadingIcon, props = __rest(_a, ["className", "primary", "secondary", "success", "warning", "danger", "outline", "textOnly", "iconButton", "switchButton", "tooltipText", "children", "clickOutsideCallback", "preventDoubleClick", "onClick", "loading", "renderLoadingIcon"]);
    var buttonRef = useRef(null);
    useOnClickOutside(buttonRef, function () {
        if (clickOutsideCallback) {
            clickOutsideCallback();
        }
    });
    var classes = ("button-component " + (className ? className : '') + " " + (primary ? 'primary' : '') + " " + (secondary ? 'secondary' : '') + " " + (outline ? 'outlined' : '') + " " + (success ? 'success' : '') + " " + (warning ? 'warning' : '') + " " + (danger ? 'danger' : '') + " " + (iconButton ? 'icon-button' : '') + " " + (tooltipText ? 'tooltip' : '') + " " + (textOnly ? 'text-only' : '') + " " + (switchButton ? 'switch-button' : '')).replace(/  +/g, ' ');
    var debouncedOnClick = debounce(clickHandler, 700, { leading: true, trailing: false });
    return (React__default.createElement("button", __assign({ ref: buttonRef }, props, { type: props.type || 'button', className: classes, onClick: preventDoubleClick ? debouncedOnClick : clickHandler }),
        tooltipText && React__default.createElement("span", { className: "tooltiptext" }, tooltipText),
        renderLoadingIcon && loading === true && renderLoadingIcon(),
        children));
    function clickHandler(event) {
        if (loading === true) {
            return;
        }
        if (onClick) {
            onClick(event);
        }
    }
};

var FormFieldWrapper = function (_a) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    return (React__default.createElement("div", __assign({ className: "form-field-wrapper " + (className || '') }, props), children));
};

var TextField = function (_a) {
    var value = _a.value, fieldName = _a.fieldName, showClearButton = _a.showClearButton, onClear = _a.onClear, children = _a.children, inputIcon = _a.inputIcon, props = __rest(_a, ["value", "fieldName", "showClearButton", "onClear", "children", "inputIcon"]);
    return (React__default.createElement(FormFieldWrapper, { className: "" + (inputIcon ? 'field-has-icon' : '') },
        inputIcon && React__default.createElement("div", { className: "input-icon" }, inputIcon),
        React__default.createElement("input", __assign({ name: fieldName, id: fieldName, autoComplete: "off", value: value || '' }, props)),
        showClearButton === true && (React__default.createElement(Button, { className: "clear-button", iconButton: true, onClick: handleClear },
            React__default.createElement("i", { className: "icon-cancel-1" }))),
        children));
    function handleClear() {
        if (onClear) {
            onClear();
        }
    }
};
var TextField$1 = React__default.memo(TextField);

var InlineFormElement = function (_a) {
    var useFullWidth = _a.useFullWidth, children = _a.children, className = _a.className, props = __rest(_a, ["useFullWidth", "children", "className"]);
    return (React__default.createElement("div", __assign({ className: "inline-form-element-wrapper " + (useFullWidth ? 'full-width' : '') + " " + (className || '') }, props), children));
};

var FormWrapper = function (_a) {
    var className = _a.className, children = _a.children, props = __rest(_a, ["className", "children"]);
    return (React__default.createElement("div", __assign({ className: "form-wrapper " + (className || '') }, props), children));
};

/**
 * @example
 * <FormLabel
 *  fieldName="fieldId">
 *  My label text
 * </FormLabel>
 */
var FormLabel = function (_a) {
    var labelText = _a.labelText, fieldName = _a.fieldName, strikeThrough = _a.strikeThrough, children = _a.children, className = _a.className, props = __rest(_a, ["labelText", "fieldName", "strikeThrough", "children", "className"]);
    if ((labelText === undefined || labelText === null || labelText === '') &&
        (children === undefined || children === null || children === '')) {
        return null;
    }
    useEffect(function () {
        if (labelText) {
            console.warn('The field `labelText` for component `FormLabel` is deprecated and replaced by children.');
        }
    }, [labelText]);
    return (React__default.createElement("label", __assign({}, props, { className: "form-field-label " + (strikeThrough ? 'strike-through-label' : '') + " " + (className || ''), htmlFor: fieldName }), children || labelText));
};

/**
 * @example
 * <FormErrorMessage>
 *  Field Required
 * </FormErrorMessage>
 */
var FormErrorMessage = function (_a) {
    var errorMessage = _a.errorMessage, className = _a.className, children = _a.children, props = __rest(_a, ["errorMessage", "className", "children"]);
    if (!errorMessage && (children === undefined || children === null || children === '')) {
        return null;
    }
    useEffect(function () {
        if (errorMessage) {
            console.warn('The field `errorMessage` for component `FormErrorMessage` is deprecated and replaced by children.');
        }
    }, [errorMessage]);
    return (React__default.createElement("span", __assign({ className: "form-field-error-message " + (className || '') }, props), children || errorMessage));
};

var FormElement = function (_a) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    return (React__default.createElement("div", __assign({ className: "form-element " + (className || '') }, props), children));
};

var FormFieldButtonWrapper = function (_a) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    return (React__default.createElement("div", __assign({ className: "form-field-button-wrapper " + (className || '') }, props), children));
};

var MultiSelectOption = function (props) {
    return (React__default.createElement("div", { className: "multiselect-option" },
        React__default.createElement("span", null, props.optionText + " "),
        React__default.createElement("span", { className: "close-button", onClick: props.removeItem },
            React__default.createElement("i", { className: "icon-cancel-1" }))));
};

// Workaround to be able to work with generic types
// mimic the functional component structure
var SelectionBox = function (_a) {
    var value = _a.value, valueKey = _a.valueKey, displayKey = _a.displayKey, searchKeys = _a.searchKeys, filterOptions = _a.filterOptions, fieldName = _a.fieldName, options = _a.options, sortOptions = _a.sortOptions, disabled = _a.disabled, onChangeValue = _a.onChangeValue, disableSearch = _a.disableSearch, renderFieldValue = _a.renderFieldValue, dropUp = _a.dropUp, onChangeText = _a.onChangeText, loading = _a.loading, emptyOption = _a.emptyOption, renderFormFieldButton = _a.renderFormFieldButton, children = _a.children, selectAllOption = _a.selectAllOption, showCreateOption = _a.showCreateOption, className = _a.className, renderOption = _a.renderOption, noOptionsMessage = _a.noOptionsMessage, showClearButton = _a.showClearButton, id = _a.id, onClear = _a.onClear, props = __rest(_a, ["value", "valueKey", "displayKey", "searchKeys", "filterOptions", "fieldName", "options", "sortOptions", "disabled", "onChangeValue", "disableSearch", "renderFieldValue", "dropUp", "onChangeText", "loading", "emptyOption", "renderFormFieldButton", "children", "selectAllOption", "showCreateOption", "className", "renderOption", "noOptionsMessage", "showClearButton", "id", "onClear"]);
    var _b = useState(false), dropdownActive = _b[0], setDropdownActive = _b[1];
    var dropdownRef = useRef(null);
    useOnClickOutside(dropdownRef, function () {
        setDropdownActive(false);
        // if the user clicks outside the dropdown AND there's no selected item
        // clears the searchValue
        if (!selectedItem) {
            setSearchValue(undefined);
        }
    });
    var _c = useState(undefined), searchValue = _c[0], setSearchValue = _c[1];
    // object type because using a generic here does not work.
    var _d = useState(undefined), selectedItem = _d[0], setSelectedItem = _d[1];
    useEffect(function () {
        var item = options.find(function (x) { return x[valueKey] === value; });
        setSelectedItem(item);
    }, [value, options]);
    useEffect(function () {
        if (onChangeText) {
            onChangeText(searchValue || '');
        }
    }, [searchValue]);
    // filter options based on userInput
    var filteredOptions = filterOptions !== false ? options.filter(filterEachOption) : options;
    // sorts the options in alphabetical order based on their displayKey
    var sortedOptions = sortOptions
        ? filteredOptions.sort(function (a, b) {
            return sortAlphabetically(a, b, displayKey);
        })
        : filteredOptions;
    var textFieldValue = selectedItem && renderFieldValue
        ? renderFieldValue(selectedItem)
        : selectedItem && selectedItem[displayKey]
            ? selectedItem[displayKey]
            : searchValue
                ? searchValue
                : value && disabled
                    ? value
                    : '';
    return (React__default.createElement("div", { ref: dropdownRef, className: "selection-box-outer-wrapper" },
        React__default.createElement(FormFieldWrapper, { className: "form-field-wrapper-select", id: id || 'teqplay-ui-select' },
            React__default.createElement(TextField$1, __assign({ className: "custom-select-input " + (className || ''), fieldName: fieldName, value: textFieldValue, disabled: disabled, readOnly: disableSearch, onKeyDown: function (keyEvent) {
                    // clears the selected item if the users touches backspace
                    if (selectedItem) {
                        if (keyEvent.keyCode === 8) {
                            setSelectedItem(undefined);
                            setSearchValue(undefined);
                            onChangeValue(null);
                        }
                    }
                }, onFocus: function () {
                    setDropdownActive(true);
                }, onChange: function (event) {
                    if (selectedItem) {
                        // if the user starts typing while there's a selected value.
                        // Clear the value completely and start with the users first typed in letter as the new search term.
                        if (event.target.value) {
                            setSearchValue(event.target.value[event.target.value.length - 1]);
                        }
                    }
                    else {
                        setSearchValue(event.target.value);
                    }
                    setSelectedItem(undefined);
                } }, props),
                dropdownActive && (React__default.createElement("div", { className: "selectionbox-options-wrapper " + (dropUp ? 'drop-up' : ''), onScroll: function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }, onWheel: function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }, id: "teqplay-ui-select-dropdown" },
                    loading && (React__default.createElement("div", { className: "selectionbox-loader" },
                        React__default.createElement("i", { className: "icon-spinner animate-spin" }))),
                    !loading && emptyOption && (React__default.createElement("div", { key: fieldName + "-" + 'empty-value', className: "selectionbox-option", onClick: function () {
                            setSelectedItem(undefined);
                            setDropdownActive(false);
                            setSearchValue(undefined);
                            if (onChangeValue) {
                                onChangeValue(null);
                            }
                        }, id: "teqplay-ui-select-empty-value" },
                        React__default.createElement("span", { className: "empty-value" }, "empty value"))),
                    !loading && showCreateOption && searchValue && (React__default.createElement("div", { key: fieldName + "-" + 'showCreateOption', className: "selectionbox-option", onClick: function () {
                            var _a;
                            var newOption = (_a = {}, _a[displayKey] = searchValue, _a[valueKey] = searchValue, _a);
                            setSelectedItem(newOption);
                            setDropdownActive(false);
                            setSearchValue(undefined);
                            if (onChangeValue) {
                                onChangeValue(newOption);
                            }
                        }, id: "teqplay-ui-select-create-option" }, "Create Option: \"" + searchValue + "\"")),
                    !loading && sortedOptions.length !== 0 && selectAllOption && !(selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.allItemsSelected) && (React__default.createElement("div", { key: fieldName + "-" + 'showAllOption', className: "selectionbox-option", onClick: function () {
                            setDropdownActive(false);
                            setSearchValue(undefined);
                            selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.onSelect();
                        }, id: "teqplay-ui-select-select-all" }, selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.optionText)),
                    !loading &&
                        sortedOptions.map(function (option, index) {
                            var isActive = option[valueKey] === ((selectedItem && selectedItem[valueKey]) || value);
                            return (React__default.createElement("div", { className: "selectionbox-option custom-option " + (isActive ? 'active' : ''), onClick: function () {
                                    setSelectedItem(option);
                                    setDropdownActive(false);
                                    setSearchValue(undefined);
                                    if (onChangeValue) {
                                        onChangeValue(option);
                                    }
                                }, key: "Dropdown-" + fieldName + "-" + index + "-" + isActive, id: "teqplay-ui-select-custom-option-" + option[displayKey] }, renderOption ? renderOption(option, index, isActive) : option[displayKey]));
                        }),
                    !loading && sortedOptions.length === 0 && !showCreateOption && !emptyOption && (React__default.createElement("div", { className: "selectionbox-option", key: fieldName + "-" + 'no-options-message', id: "teqplay-ui-select-no-results" }, noOptionsMessage !== undefined
                        ? noOptionsMessage
                        : "No results " + (searchValue ? "for " + searchValue : 'found'))))),
                showClearButton === true && !disabled && (React__default.createElement(Button, { iconButton: true, className: "clear-button", onClick: function () {
                        setSelectedItem(undefined);
                        setSearchValue(undefined);
                        onChangeValue(null);
                        setDropdownActive(false);
                        if (onClear) {
                            onClear();
                        }
                    } },
                    React__default.createElement("i", { className: "icon-cancel-1" }))),
                !disabled && (React__default.createElement(Button, { iconButton: true, onClick: function () {
                        setDropdownActive(!dropdownActive);
                        setSearchValue(undefined);
                    } }, dropdownActive ? React__default.createElement("i", { className: "icon-up-open" }) : React__default.createElement("i", { className: "icon-down-open" })))),
            renderFormFieldButton ? renderFormFieldButton() : null),
        children));
    function filterEachOption(value) {
        if (!value) {
            return;
        }
        if (searchValue === '' || searchValue === undefined) {
            return true;
        }
        if (searchKeys && searchKeys.length) {
            // Multiple keys are passed to have a match on
            var keysThatHaveAMatch = searchKeys.filter(function (searchKey) {
                var valueToFilter = value[searchKey];
                return valueToFilter && valueToFilter.toLowerCase().includes(searchValue.toLowerCase());
            });
            return keysThatHaveAMatch.length ? true : false;
        }
        else if (displayKey) {
            // Filter based on single display key
            var valueToFilter = value[displayKey];
            return valueToFilter && valueToFilter.toLowerCase().includes(searchValue.toLowerCase());
        }
    }
    function sortAlphabetically(a, b, propertyToSort) {
        var _a, _b;
        a = ((_a = a[propertyToSort]) === null || _a === void 0 ? void 0 : _a.toString().toLowerCase()) || '';
        b = ((_b = b[propertyToSort]) === null || _b === void 0 ? void 0 : _b.toString().toLowerCase()) || '';
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }
};

var MultiSelectionBox = function (_a) {
    var options = _a.options, values = _a.values, valueKey = _a.valueKey, fieldName = _a.fieldName, searchKey = _a.searchKey, selectAllOption = _a.selectAllOption, onChangeValue = _a.onChangeValue, renderFieldValue = _a.renderFieldValue, onClearAll = _a.onClearAll, props = __rest(_a, ["options", "values", "valueKey", "fieldName", "searchKey", "selectAllOption", "onChangeValue", "renderFieldValue", "onClearAll"]);
    // filter out any values which have already been selected.
    var nonSelectedOptions = options.filter(function (option) {
        return !(values === null || values === void 0 ? void 0 : values.find(function (x) { return x[valueKey] === option[valueKey]; }));
    });
    return (React__default.createElement(SelectionBox, __assign({ fieldName: fieldName, valueKey: valueKey, displayKey: searchKey, renderFieldValue: function () {
            return '';
        }, selectAllOption: selectAllOption
            ? __assign(__assign({}, selectAllOption), { onSelect: function () {
                    onChangeValue(options);
                }, allItemsSelected: (values === null || values === void 0 ? void 0 : values.length) === options.length }) : undefined, onChangeValue: function (selectedItem) {
            if (selectedItem) {
                if (values) {
                    onChangeValue(__spreadArrays(values).concat(selectedItem));
                }
                else {
                    onChangeValue([selectedItem]);
                }
            }
        }, onClear: function () {
            onChangeValue([]);
            if (onClearAll) {
                onClearAll();
            }
        }, options: nonSelectedOptions }, props, { value: '' }), values && (React__default.createElement("div", { className: "multiselect-options-wrapper" }, values.map(function (value, index) {
        if (renderFieldValue) {
            return React__default.createElement("div", { key: fieldName + "-" + index }, renderFieldValue(value, removeOption));
        }
        else {
            return searchKey ? value[searchKey] : value[valueKey];
        }
    })))));
    function removeOption(option) {
        // remove the selected item from the selection array.
        var newOptions = values ? values.filter(function (x) { return x[valueKey] !== option[valueKey]; }) : [];
        onChangeValue(newOptions);
    }
};

var Checkbox = function (_a) {
    var className = _a.className, field = _a.field, label = _a.label, labelClassName = _a.labelClassName, strikeThrough = _a.strikeThrough, value = _a.value, props = __rest(_a, ["className", "field", "label", "labelClassName", "strikeThrough", "value"]);
    return (createElement("div", { className: "checkbox " + (className || '') },
        createElement("div", { className: "custom-checkbox" },
            createElement("input", __assign({ className: "input" + (value && value.length > 0 ? ' labelled' : '') + (props.disabled ? ' disabled' : ''), type: 'checkbox', name: field, id: field }, props, { checked: props.checked || value })),
            createElement("label", { className: "form-field-label " + (strikeThrough ? 'strike-through-label' : '') + " label " + (label && label.length > 0 ? '' : 'label-hidden') + " " + (props.disabled ? 'disabled' : '') + " " + (labelClassName || ''), htmlFor: field }, label && label.length ? label : ''),
            createElement(FormLabel, { className: "label" + (label && label.length > 0 ? '' : ' label-hidden') + (props.disabled ? ' disabled' : '') + " " + (labelClassName || ''), fieldName: field }))));
};

/**
 * @example
 * <Dialog
      title={'This is a Dialog Title'}
      type={'small'}
      showDialog={showDialog}
      onCloseDialog={() => {
        setShowDialog(false)
      }}
      footerContent={
        <ButtonGroup>
          <Button primary>
            Primary
          </Button>
        </ButtonGroup>
      }
    >
      <div>Content</div>
    </Dialog>
 */
/**
 * ⚠️ **Scrolling inside this Dialog component is broken for iOS devices** ⚠️
 */
var Dialog = function (_a) {
    var className = _a.className, titleIcon = _a.titleIcon, title = _a.title, type = _a.type, showDialog = _a.showDialog, center = _a.center, onCloseDialog = _a.onCloseDialog, contentClassName = _a.contentClassName, footerContent = _a.footerContent, children = _a.children, disableBodyScrollChecker = _a.disableBodyScrollChecker, props = __rest(_a, ["className", "titleIcon", "title", "type", "showDialog", "center", "onCloseDialog", "contentClassName", "footerContent", "children", "disableBodyScrollChecker"]);
    var options = {
        reserveScrollBarGap: true,
    };
    var dialogRef = useRef(null);
    useEffect(function () {
        if (showDialog) {
            // This disables any scrolling inside the Dialog for iOS devices
            //@ts-ignore
            disableBodyScroll(dialogRef, options);
        }
        else {
            //@ts-ignore
            enableBodyScroll(dialogRef);
        }
        return function cleanup() {
            //@ts-ignore
            enableBodyScroll(dialogRef);
        };
    }, [showDialog]);
    if (!showDialog) {
        return null;
    }
    return (React__default.createElement("div", __assign({ className: "dialog " + (center ? 'vertical-center' : '') + " " + (className || '') }, props),
        React__default.createElement("div", { className: "dialog-overlay", onClick: function () {
                onCloseDialog();
            } },
            React__default.createElement("div", { ref: dialogRef, className: "dialog-box " + (type || 'small'), onClick: function (e) { return e.stopPropagation(); } },
                !props.hideHeader && (React__default.createElement("div", { className: "dialog-header" },
                    React__default.createElement("h2", null,
                        titleIcon,
                        " ",
                        title),
                    React__default.createElement("div", { className: "close-dialog-button", onClick: onCloseDialog },
                        React__default.createElement("i", { className: "icon-cancel-1" })))),
                React__default.createElement("div", { className: "dialog-content " + (contentClassName ? contentClassName : 'padded-content') }, children),
                footerContent && React__default.createElement("div", { className: "dialog-footer" }, footerContent)))));
};

var DropDownButton = function (_a) {
    var renderCustomIcon = _a.renderCustomIcon, buttonText = _a.buttonText, preventClosingOnValueSelect = _a.preventClosingOnValueSelect, props = __rest(_a, ["renderCustomIcon", "buttonText", "preventClosingOnValueSelect"]);
    var _b = useState(false), dropdownActive = _b[0], setDropdownActive = _b[1];
    return (React__default.createElement(Button, __assign({}, props, { clickOutsideCallback: closeDropDown, onClick: function () {
            setDropdownActive(!dropdownActive);
        } }),
        renderCustomIcon ? (renderCustomIcon(dropdownActive)) : dropdownActive ? (React__default.createElement("i", { className: "icon-up-open" })) : (React__default.createElement("i", { className: "icon-down-open" })),
        buttonText || null,
        dropdownActive && (React__default.createElement("div", { className: "dropdown-button-content", onClick: clickDropdownContent }, props.children))));
    function closeDropDown() {
        setDropdownActive(false);
    }
    function clickDropdownContent(e) {
        e.stopPropagation();
        if (preventClosingOnValueSelect !== true) {
            closeDropDown();
        }
    }
};

var DropDownButtonItem = function (_a) {
    var onClick = _a.onClick, children = _a.children, props = __rest(_a, ["onClick", "children"]);
    return (React__default.createElement("div", __assign({ className: "dropdown-button-item", onClick: function (e) {
            if (onClick) {
                onClick(e);
            }
        } }, props), children));
};

var TextArea = function (_a) {
    var fieldName = _a.fieldName, props = __rest(_a, ["fieldName"]);
    return (React__default.createElement(FormFieldWrapper, null,
        React__default.createElement("textarea", __assign({ id: fieldName, name: fieldName }, props))));
};

var Collapse = function (_a) {
    var isOpen = _a.isOpen, children = _a.children, className = _a.className, collapseHeaderContent = _a.collapseHeaderContent, props = __rest(_a, ["isOpen", "children", "className", "collapseHeaderContent"]);
    var _b = useState(isOpen ? isOpen : false), collapsed = _b[0], setCollapsed = _b[1];
    var _c = useMeasure(), ref = _c[0], height = _c[1].height;
    var toggleProps = useSpring({ height: collapsed ? height + "px" : '0px' });
    useEffect(function () {
        if ((collapsed !== isOpen && isOpen === false) || isOpen === true) {
            setCollapsed(isOpen);
        }
    }, [isOpen]);
    return (React__default.createElement("div", __assign({}, props, { className: "collapse-element " + (className || '') + " " + (collapsed ? 'collapsed-element' : '') }),
        React__default.createElement("div", { className: "collapse-bar " + (collapsed ? 'collapsed' : ''), onClick: function () {
                setCollapsed(!collapsed);
            } },
            React__default.createElement("div", { className: "collapse-bar-content" }, collapseHeaderContent ? collapseHeaderContent() : null),
            React__default.createElement("div", { className: "collapse-control-icon " + (collapsed ? 'collapsed-icon' : '') },
                React__default.createElement("i", { className: "icon-down-open" }))),
        React__default.createElement(animated.div, { className: "collapse-content " + (collapsed ? 'visible-content' : ''), style: toggleProps },
            React__default.createElement("div", { ref: ref }, children))));
};

var defaultStore = {
    onTabClick: function (tabId) { return null; },
    activeTab: undefined
};
var TabContext = React__default.createContext(defaultStore);

//
// Tabs main-component
//
var Tabs = function (_a) {
    var children = _a.children, initialActiveTab = _a.initialActiveTab, onTabChange = _a.onTabChange, props = __rest(_a, ["children", "initialActiveTab", "onTabChange"]);
    var _b = useState(initialActiveTab), activeTab = _b[0], setActiveTab = _b[1];
    useEffect(function () {
        if (initialActiveTab !== activeTab) {
            setActiveTab(initialActiveTab);
        }
    }, [initialActiveTab]);
    return (React__default.createElement(TabContext.Provider, { value: { onTabClick: onTabClick, activeTab: activeTab } },
        React__default.createElement("div", __assign({ className: "tabs", id: "teqplay-ui-tabs" }, props), children({ activeTab: activeTab, setActiveTab: setActiveTab }))));
    function onTabClick(tabId) {
        // Set in state
        setActiveTab(tabId);
        // Send to user
        if (onTabChange) {
            onTabChange(tabId);
        }
    }
};
//
// Button sub-component
//
var Button$1 = function (props) {
    var tabId = props.tabId, className = props.className, otherProps = __rest(props, ["tabId", "className"]);
    return (React__default.createElement(TabContext.Consumer, null, function (_a) {
        var onTabClick = _a.onTabClick, activeTab = _a.activeTab;
        return (React__default.createElement(Button, __assign({ className: "tabs-button " + (activeTab === props.tabId ? 'primary' : ''), onClick: function () { return onTabClick(props.tabId); } }, otherProps), props.children));
    }));
};
Tabs.Button = Button$1;

var DATE_TIME_FORMAT = 'dd MMM yyyy HH:mm';
function isInputValidDate(value, timeZone) {
    var date = dateStringToDateObject(value, timeZone);
    if (!date) {
        return false;
    }
    var isValid = !isNaN(date.getTime());
    return isValid;
}
function dateStringToDateObject(value, timeZone) {
    var isDateIncludingTime = value.length === 16;
    if ((value.length === 10 || isDateIncludingTime) && !value.includes('_')) {
        var hour = isDateIncludingTime ? Number(value.slice(11, 13)) : undefined;
        var minute = isDateIncludingTime ? Number(value.slice(14, 16)) : undefined;
        var year = Number(value.slice(6, 10));
        var month = Number(value.slice(3, 5));
        var day = Number(value.slice(0, 2));
        if (isNaN(year) ||
            isNaN(month) ||
            isNaN(day) ||
            isNaN(year) ||
            (isDateIncludingTime && ((hour && isNaN(hour)) || (minute && isNaN(minute))))) {
            return null;
        }
        return DateTime.fromObject({
            year: year,
            month: month,
            day: day,
            hour: hour,
            minute: minute,
            zone: timeZone,
        })
            .toUTC()
            .toJSDate();
    }
    return null;
}
function dateStringToIso(value, timeZone) {
    if ((value.length === 10 || value.length === 16) && !value.includes('_')) {
        var hours = value.length === 16 ? Number(value.slice(11, 13)) : undefined;
        var minutes = value.length === 16 ? Number(value.slice(14, 16)) : undefined;
        return DateTime.fromObject({
            year: Number(value.slice(6, 10)),
            month: Number(value.slice(3, 5)),
            day: Number(value.slice(0, 2)),
            hour: hours,
            minute: minutes,
            zone: timeZone,
        })
            .toUTC()
            .toISO();
    }
    return null;
}
function isoStringToFormat(value, format, timeZone) {
    var date = DateTime.fromISO(value);
    if (timeZone) {
        date = date.setZone(timeZone);
    }
    else {
        date = date.toLocal();
    }
    return date.toFormat(format, { timeZone: timeZone });
}
function formatJsDatetoDateText(date, locale, timeZone, monthFull, dateSeperator) {
    if (monthFull === void 0) { monthFull = true; }
    if (dateSeperator === void 0) { dateSeperator = ' '; }
    var dateObject = DateTime.fromObject({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        zone: timeZone,
    }).reconfigure(locale ? { locale: locale } : {});
    if (timeZone) {
        dateObject = dateObject.setZone(timeZone);
    }
    else {
        dateObject = dateObject.toLocal();
    }
    return dateObject.toFormat("dd" + dateSeperator + (monthFull ? 'LLL' : 'LL') + dateSeperator + "yyyy", { timeZone: timeZone });
}
function formatJsDatetoTimeText(date, timeSeperator, hideHourLeadingZero) {
    if (timeSeperator === void 0) { timeSeperator = ':'; }
    if (hideHourLeadingZero === void 0) { hideHourLeadingZero = false; }
    var dateObject = DateTime.fromObject({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
    });
    return dateObject.toFormat("" + (hideHourLeadingZero ? 'H' : 'HH') + timeSeperator + "mm");
}
// Accepts a date string and then returns a JS Date with the time offset of the specified timezone applied
function dateStringToZonedJSDate(dateString, timeZone, format) {
    if (format === void 0) { format = DATE_TIME_FORMAT; }
    // if a timezone is applied it uses this, otherwise use local timezone
    var timezoneToApply = timeZone ? timeZone : 'local';
    // parse the dateString and apply the timezone to this
    var parsedDate = DateTime.fromISO(dateString, { zone: timezoneToApply });
    // formats the parsedDate to a string without tz information and then puts it into the date object
    // This makes sure you get a js date object with the time offsets applied to it.
    var zonedDate = new Date(parsedDate.toFormat(format));
    return zonedDate;
}
// Given a JS Date, parse it to the supplied timezone and then return the formatted value
function formatJSDateWithZone(time, format, timeZone) {
    // if a timezone is applied it uses this, otherwise use local timezone
    var zoneToUse = timeZone ? timeZone : 'local';
    // parse the js date to a luxon DateTime object
    var parsedTime = DateTime.fromJSDate(time);
    // convert the date to a zoned date
    var zonedTime = parsedTime.setZone(zoneToUse);
    // return the date in the specified format with the timezone offset applied
    return zonedTime.toFormat(format);
}
function applyTimezoneOffsetToJsDate(date, timeZone, format) {
    if (format === void 0) { format = DATE_TIME_FORMAT; }
    // if a timezone is applied it uses this, otherwise use local timezone
    var timezoneToApply = timeZone ? timeZone : 'local';
    // parse the js Date to a luxon DateTime object
    var parsedDate = DateTime.fromJSDate(date);
    // apply the timezone to the luxon DateTime
    var zonedDate = parsedDate.setZone(timezoneToApply);
    // parse the date with timezones applied to the safe parsing format without tz information
    var zonedDateInSafeFormat = zonedDate.toFormat(format);
    // construct a js date object given the safely formatted date string
    var jsDate = new Date(zonedDateInSafeFormat);
    // return the js date with the timezone offset applied
    return jsDate;
}
function getMonthAsNumber(monthName) {
    return {
        January: '01',
        February: '02',
        March: '03',
        April: '04',
        May: '05',
        June: '06',
        July: '07',
        August: '08',
        September: '09',
        October: '10',
        November: '11',
        December: '12',
    }[monthName];
}
function calculateMinMonthDate(yearMonth, timeZone) {
    if (timeZone === void 0) { timeZone = 'UTC'; }
    var year = yearMonth.split('-')[0];
    var month = yearMonth.split('-')[1];
    // create a DateTime Luxon Object with the given year and month
    var parsedYearMonth = DateTime.fromObject({
        year: Number(year),
        month: Number(month),
        day: 1,
        zone: timeZone,
    });
    // return the date time with timezone applied as a js Date
    return parsedYearMonth.toJSDate();
}
function calculateMaxMonthDate(yearMonth, timeZone) {
    if (timeZone === void 0) { timeZone = 'UTC'; }
    var year = Number(yearMonth.split('-')[0]);
    var month = Number(yearMonth.split('-')[1]);
    var nextMonth = month < 12 ? month + 1 : 1;
    var nextYear = month < 12 ? year : year + 1;
    // construct the date based on year, month, day and timezone
    var parsedYearMonth = DateTime.fromObject({
        year: nextYear,
        month: nextMonth,
        day: 1,
        zone: timeZone,
    });
    // subtract a milisecond from the month to make it in the last milisecond of the month before
    var lastMomentOfMonth = parsedYearMonth.minus({ milliseconds: 1 });
    return lastMomentOfMonth.toJSDate();
}
function formatDateTime(time, timeZone, format) {
    if (format === void 0) { format = DATE_TIME_FORMAT; }
    if (!time) {
        return '';
    }
    var formatToApply = format;
    return returnFormattedDateTime(time, formatToApply, timeZone);
}
function formatDate(time, timeZone, dateFormat) {
    var formatToApply = dateFormat ? dateFormat : 'dd MMM yyyy';
    return returnFormattedDateTime(time, formatToApply, timeZone);
}
function formatTime(time, timeZone) {
    var formatToApply = 'HH:mm';
    return returnFormattedDateTime(time, formatToApply, timeZone);
}
function formatDateWithoutYear(time, timeZone) {
    var formatToApply = 'dd MMM';
    return returnFormattedDateTime(time, formatToApply, timeZone);
}
function isBefore(dateTime, dateTimeToCompare) {
    return dateTime < dateTimeToCompare;
}
function datesAreEqual(dateTime, dateTimeToCompare) {
    return dateTime.equals(dateTimeToCompare);
}
function isAfter(dateTime, dateTimeToCompare) {
    return dateTime > dateTimeToCompare;
}
function isSameMonth(dateTime, dateTimeToCompare) {
    return dateTime.month === dateTimeToCompare.month;
}
function isSameYear(dateTime, dateTimeToCompare) {
    return dateTime.year === dateTimeToCompare.year;
}
function isSameDay(dateTime, dateTimeToCompare) {
    return dateTime.day === dateTimeToCompare.day;
}
function isSameDayAndMonth(dateTime, dateTimeToCompare) {
    return isSameDay(dateTime, dateTimeToCompare) && isSameYear(dateTime, dateTimeToCompare);
}
function isExactSameDay(dateTime, dateTimeToCompare) {
    return (isSameDay(dateTime, dateTimeToCompare) &&
        isSameMonth(dateTime, dateTimeToCompare) &&
        isSameYear(dateTime, dateTimeToCompare));
}
function isToday(dateTime) {
    return isExactSameDay(DateTime.local(), dateTime);
}
function isInThePast(dateTime) {
    return dateTime < DateTime.local();
}
function isInTheFuture(dateTime) {
    return dateTime > DateTime.local();
}
function isValidDateTime(dateOrString) {
    var parsedDate = parseDateOrStringToDateTime(dateOrString);
    return parsedDate.isValid;
}
function parseDateOrStringToFormat(dateOrString, format, timeZone) {
    var formattedDate = parseDateOrStringToDateTime(dateOrString).toFormat(format, { timeZone: timeZone });
    return formattedDate;
}
function parseDateOrStringToDateTime(dateOrString) {
    var parsedDate = dateOrString instanceof Date ? DateTime.fromJSDate(dateOrString) : DateTime.fromISO(dateOrString);
    return parsedDate;
}
function parseJSDateOrStringWithTimezone(dateOrString, timeZone) {
    // if a timezone is applied it uses this, otherwise use local timezone
    var timezoneToApply = timeZone ? timeZone : 'local';
    // based on the type of the time it parses it as a iso string or a js date
    var parsedDate = parseDateOrStringToDateTime(dateOrString);
    // apply the timezone to the parsed date
    var parsedWithZone = parsedDate.setZone(timezoneToApply);
    return parsedWithZone;
}
function returnFormattedDateTime(time, dateFormat, timeZone) {
    if (!time) {
        return '';
    }
    // get the parsed DateTime object with the corrected timezone
    var parsedDateOrString = parseJSDateOrStringWithTimezone(time, timeZone);
    // Format the DateTime object to the specified format
    var formattedTime = parsedDateOrString.toFormat(dateFormat);
    // return the formatted string
    return formattedTime;
}
function isValidDate(date) {
    if (date) {
        var dateTime = parseDateOrStringToDateTime(date);
        return dateTime.isValid;
    }
    else {
        return false;
    }
}
function snapToHour(date) {
    var parsedDate = DateTime.fromJSDate(date);
    return parsedDate.startOf('hour').toJSDate();
}
function isTimeInputValid(time, includeSeconds, seperator) {
    if ((time.length === 5 || time.length === 8) && !time.includes('_')) {
        var hours = time.split(seperator)[0];
        var minutes = time.split(seperator)[1];
        var seconds = time.split(seperator)[2];
        // Check if hours are invalid
        if (hours.length !== 2 || Number(hours) < 0 || Number(hours) >= 24) {
            // hours are not valid so time is not valid
            return false;
        }
        // Check if minutes are valid
        if (minutes.length !== 2 || Number(minutes) < 0 || Number(minutes) >= 60) {
            // hours are not valid so time is not valid
            return false;
        }
        // Check if seconds are invalid
        if (includeSeconds && (seconds.length !== 2 || Number(seconds) < 0 || Number(seconds) > 60)) {
            // seconds is not valid so time is not valid
            return false;
        }
        // All inputs are valid
        return true;
    }
    // The time string is not the right length
    return false;
}

var YearMonthDropdown = function (props) {
    var months = props.localeUtils.getMonths();
    var currentYear = new Date().getFullYear();
    var fromMonth = new Date(currentYear, 0);
    var toMonth = new Date(currentYear + 10, 11);
    var initialYear = props.startYear || fromMonth.getFullYear();
    var yearLimit = props.endYear || (props.startYear && props.startYear + 10) || toMonth.getFullYear();
    if (props.endYear && props.startYear && props.endYear < props.startYear) {
        console.error('"endYear" cannot be smaller than "startYear"');
        return null;
    }
    var years = range(initialYear, yearLimit + 1);
    var handleChange = function (e) {
        var _a = e.target.form, year = _a.year, month = _a.month;
        props.onChange(new Date(year.value, month.value));
    };
    return (React__default.createElement("form", { className: "DayPicker-Caption DayPicker-YearMonthDropdown" },
        React__default.createElement("select", { name: "month", onChange: handleChange, value: props.date.getMonth() }, months.map(function (month, i) { return (React__default.createElement("option", { key: month, value: i }, month)); })),
        React__default.createElement("select", { name: "year", onChange: handleChange, value: props.date.getFullYear() }, years.map(function (year) { return (React__default.createElement("option", { key: year, value: year }, year)); }))));
};

var DateDialog = function (props) {
    var _a = useState(function () { return getInitialDateValue(); }), selectedDate = _a[0], setSelectedDate = _a[1];
    var _b = useState(), month = _b[0], setMonth = _b[1];
    var dateToShow = selectedDate || props.activeDateValue;
    var jsDateToShow = dateToShow ? DateTime.fromISO(dateToShow).toUTC().toJSDate() : undefined;
    useUpdateEffect(function () {
        if (props.activeDateValue) {
            setSelectedDate(props.activeDateValue);
        }
    }, [props.activeDateValue]);
    return (React__default.createElement(Dialog, { title: props.dateSelectDialogTitle, showDialog: props.showDateDialog, type: "flexible", onCloseDialog: props.closeDateDialog, hideHeader: props.hideDialogHeader, id: props.dialogId || 'teqplay-ui-dialog-date', footerContent: React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Button, { onClick: props.closeDateDialog }, "Cancel"),
            React__default.createElement(Button, { primary: true, onClick: onSave, disabled: !selectedDate }, "Ok")) },
        React__default.createElement("div", { className: "active-date-time" },
            React__default.createElement("span", { className: "active" }, jsDateToShow ? formatJsDatetoDateText(jsDateToShow, props.locale) : '')),
        React__default.createElement("div", { className: "padded-content date-picker-container" },
            React__default.createElement(DayPicker, { containerProps: { id: props.datePickerId || 'teqplay-ui-date-picker' }, onDayClick: onDateClick, initialMonth: jsDateToShow, month: month, locale: props.locale, modifiers: {
                    highlighted: jsDateToShow,
                }, captionElement: props.showMonthYearDropdown
                    ? function (_a) {
                        var date = _a.date, localeUtils = _a.localeUtils;
                        return (React__default.createElement(YearMonthDropdown, { date: date, localeUtils: localeUtils, onChange: function (date) { return setMonth(date); }, startYear: props.startYear, endYear: props.endYear }));
                    }
                    : undefined }))));
    function getInitialDateValue() {
        // If a date is known don't fill the state, in the code it will use the prop
        // If it doens't have a date it should fill in a new Date object
        return props.activeDateValue ? props.activeDateValue : DateTime.local().toUTC().toISO();
    }
    function onDateClick(date) {
        var _a = props.activeDateValue ? DateTime.fromISO(props.activeDateValue) : { minute: 0, hour: 0 }, minute = _a.minute, hour = _a.hour;
        var dateWithSetHourAndMinute = DateTime.fromObject({
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
            hour: hour,
            minute: minute,
        })
            .toUTC()
            .toISO();
        setSelectedDate(dateWithSetHourAndMinute);
    }
    function onSave() {
        if (selectedDate) {
            props.onDateSave(selectedDate);
        }
        else {
            // Should never happen
            props.closeDateDialog();
        }
    }
};

var DatePicker = function (_a) {
    var _b = _a.dateSeperator, dateSeperator = _b === void 0 ? '/' : _b, onDateChange = _a.onDateChange, _c = _a.dialogTitle, dialogTitle = _c === void 0 ? 'Select Date' : _c, locale = _a.locale, timeZone = _a.timeZone, initialDate = _a.initialDate, icon = _a.icon, hideDialogHeader = _a.hideDialogHeader, dialogId = _a.dialogId, id = _a.id, datePickerId = _a.datePickerId, showMonthYearDropdown = _a.showMonthYearDropdown, startYear = _a.startYear, endYear = _a.endYear, props = __rest(_a, ["dateSeperator", "onDateChange", "dialogTitle", "locale", "timeZone", "initialDate", "icon", "hideDialogHeader", "dialogId", "id", "datePickerId", "showMonthYearDropdown", "startYear", "endYear"]);
    var format = "dd" + dateSeperator + "LL" + dateSeperator + "yyyy HH:mm";
    var prevTimeZone = usePrevious(timeZone);
    var _d = useState(''), value = _d[0], setValue = _d[1];
    useEffect(function () {
        // default value, if initialDate is not defined, we use a empty string
        var newValue = '';
        if (props.value && props.value !== '') {
            if (DateTime.fromISO(props.value).isValid) {
                // the value supplied a valid isoDate so we parse that into the internal value format
                newValue = isoStringToFormat(props.value, format, timeZone);
            }
            else {
                // means we are dealing with a invalid iso string
                // so we can safely assume it's the format used by the inputmask
                newValue = props.value;
            }
        }
        if (value !== newValue) {
            setValue(newValue);
        }
    }, [props.value]);
    useUpdateEffect(function () {
        // Reset value back to iso based on prev timezon, than apply new timezone
        var isValid = isInputValidDate(value, prevTimeZone);
        var isoStringBasedOnTimezone = dateStringToIso(value, prevTimeZone);
        if (isValid && isoStringBasedOnTimezone) {
            setValue(isoStringToFormat(isoStringBasedOnTimezone, format, timeZone));
        }
    }, [timeZone]);
    var _e = useState(false), showDatePicker = _e[0], setShowDatePicker = _e[1];
    var datePickerRef = useRef(null);
    useOnClickOutside(datePickerRef, function () {
        setShowDatePicker(false);
    });
    return (React__default.createElement("div", { className: "date-time-picker-container", ref: datePickerRef },
        React__default.createElement(FormFieldWrapper, { id: id || 'teqplay-ui-date-picker' },
            React__default.createElement(InputMask, __assign({}, props, { mask: "99" + dateSeperator + "99" + dateSeperator + "9999", value: value, onChange: onValueChange })),
            !props.disabled && (React__default.createElement("span", { className: "date-icon-container", onClick: function () { return setShowDatePicker(!showDatePicker); } }, icon))),
        React__default.createElement(DateDialog, { onDateSave: onDateSelect, showDateDialog: showDatePicker, closeDateDialog: function () { return setShowDatePicker(false); }, activeDateValue: isInputValidDate(value) ? dateStringToIso(value) || undefined : undefined, dateSelectDialogTitle: dialogTitle, locale: locale, hideDialogHeader: hideDialogHeader, dialogId: dialogId, datePickerId: datePickerId, showMonthYearDropdown: showMonthYearDropdown, startYear: startYear, endYear: endYear })));
    function onDateSelect(isoString) {
        // Get string value dd/mm/yyyy
        var newInputValue = isoStringToFormat(isoString, format);
        // Hide date picker
        setShowDatePicker(false);
        // Set value inside input field
        setValue(newInputValue);
        // Send to parent component
        var _a = DateTime.fromISO(isoString, { zone: timeZone }).toObject(), day = _a.day, month = _a.month, year = _a.year;
        var newIsoString = DateTime.fromObject({ year: year, month: month, day: day, hour: 0, minute: 0, zone: timeZone }).toUTC().toISO();
        onDateChange(newIsoString, newInputValue, true, props.name);
    }
    function onValueChange(e) {
        var inputValue = e.target.value;
        setValue(inputValue);
        if (inputValue.length === 10 && !inputValue.includes('_')) {
            // switch date and month
            var isValid = isInputValidDate(inputValue);
            onDateChange(isValid
                ? DateTime.fromObject({
                    minute: 0,
                    hour: 0,
                    day: parseInt(inputValue.slice(0, 2)),
                    month: parseInt(inputValue.slice(3, 5)),
                    year: parseInt(inputValue.slice(6, 10)),
                    zone: timeZone,
                })
                    .toUTC()
                    .toISO()
                : null, inputValue, isValid, props.name);
            return;
        }
        onDateChange(null, inputValue, false, props.name);
    }
};

var TimeDialog = function (props) {
    var _a = useState(null), selectedTime = _a[0], setSelectedTime = _a[1];
    useEffect(function () {
        // If state changed in showing dialog, reset the selected date
        if (selectedTime) {
            setSelectedTime(null);
        }
    }, [props.showTimeDialog]);
    return (React__default.createElement(Dialog, { title: props.timeSelectDialogTitle, showDialog: props.showTimeDialog, hideHeader: props.hideDialogHeader, type: "flexible", className: "time-dialog", id: props.dialogId || 'teqplay-ui-dialog-time', onCloseDialog: props.closeTimeDialog, footerContent: React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Button, { onClick: props.closeTimeDialog }, "Cancel"),
            React__default.createElement(Button, { primary: true, onClick: onSave, disabled: !selectedTime }, "Ok")) },
        React__default.createElement(TimeKeeper, { onChange: onTimeSelect, time: selectedTime || props.activeTimeValue, hour24Mode: true, switchToMinuteOnHourSelect: true })));
    function onTimeSelect(_a) {
        var formatted24 = _a.formatted24;
        setSelectedTime(formatted24);
    }
    function onSave() {
        if (selectedTime) {
            props.onTimeSave(selectedTime);
        }
        else {
            // Should never happen
            props.closeTimeDialog();
        }
    }
};

var TimePicker = function (_a) {
    var _b = _a.timeSeperator, timeSeperator = _b === void 0 ? ':' : _b, onTimeChange = _a.onTimeChange, _c = _a.includeSeconds, includeSeconds = _c === void 0 ? false : _c, _d = _a.dialogTitle, dialogTitle = _d === void 0 ? 'Select time' : _d, icon = _a.icon, hideDialogHeader = _a.hideDialogHeader, dialogId = _a.dialogId, id = _a.id, props = __rest(_a, ["timeSeperator", "onTimeChange", "includeSeconds", "dialogTitle", "icon", "hideDialogHeader", "dialogId", "id"]);
    var _e = useState(props.value || ''), value = _e[0], setValue = _e[1];
    var _f = useState(false), showTimePicker = _f[0], setShowTimePicker = _f[1];
    var toggleTimeDialog = function () { return setShowTimePicker(!showTimePicker); };
    var timePickerRef = useRef(null);
    useUpdateEffect(function () {
        if (value !== props.value) {
            setValue(props.value || '');
        }
    }, [props.value]);
    useOnClickOutside(timePickerRef, function () {
        setShowTimePicker(false);
    });
    return (React__default.createElement("div", { className: "date-time-picker-container", ref: timePickerRef },
        React__default.createElement(FormFieldWrapper, { id: id || 'teqplay-ui-time-picker' },
            React__default.createElement(InputMask, { mask: "99" + timeSeperator + "99", value: value, onChange: onValueChange }),
            !props.disabled && icon && (React__default.createElement("span", { className: "time-icon-container", onClick: function () { return setShowTimePicker(!showTimePicker); } }, icon))),
        React__default.createElement(TimeDialog, { showTimeDialog: showTimePicker, onTimeSave: onTimeSelect, closeTimeDialog: toggleTimeDialog, timeSelectDialogTitle: dialogTitle, activeTimeValue: value ? String(value) : undefined, hideDialogHeader: hideDialogHeader, dialogId: dialogId })));
    function onTimeSelect(time) {
        var formattedTime = time.padStart(5, '0');
        // Hide date picker
        setShowTimePicker(false);
        // Send to parent component
        onTimeChange(formattedTime, true, props.name);
        // Set value inside input field
        setValue(formattedTime);
    }
    function onValueChange(e) {
        var inputValue = e.target.value;
        sendChangeToUser(inputValue);
        setValue(inputValue);
    }
    function sendChangeToUser(inputValue) {
        onTimeChange(inputValue, isTimeInputValid(inputValue, includeSeconds || false, timeSeperator), props.name);
    }
};
var TimePicker$1 = React__default.memo(TimePicker);

var TimesListSelect = function (_a) {
    var _b = _a.step, step = _b === void 0 ? 15 : _b, startLimit = _a.startLimit, endLimit = _a.endLimit, value = _a.value, id = _a.id, onSelect = _a.onSelect;
    var _c = useState(false), selectedFromList = _c[0], setSelectedFromList = _c[1];
    var _d = useState([]), options = _d[0], setOptions = _d[1];
    var _e = useState(false), scrolledToItemOnce = _e[0], setScrolledToItemOnce = _e[1];
    var valueString = value && String(value.hour).padStart(2, '0') + ":" + String(value.minute).padStart(2, '0');
    var startLimitString = startLimit && String(startLimit.hour).padStart(2, '0') + ":" + String(startLimit.minute).padStart(2, '0');
    var endLimitString = endLimit && String(endLimit.hour).padStart(2, '0') + ":" + String(endLimit.minute).padStart(2, '0');
    // Fill the option values
    useEffect(function () {
        var newOptions = getOptions();
        setOptions(newOptions);
    }, [startLimit, endLimit]);
    // Check if 'value' is valid
    useEffect(function () {
        checkIfValueIsValid(value);
        if (!selectedFromList && value) {
            getOptions();
        }
    }, [value]);
    return (React__default.createElement("div", { className: "times-list-select", id: "times-list-select " + (id || '') }, options.map(function (option) { return (React__default.createElement("button", { type: "button", className: "times-list-button " + (valueString === option ? 'active' : ''), ref: function (ref) {
            // Scroll once to the intial value, if no value scroll to 09:00
            if (!scrolledToItemOnce && (valueString || '09:00') === option) {
                ref === null || ref === void 0 ? void 0 : ref.scrollIntoView();
                setScrolledToItemOnce(true);
            }
        }, onClick: function (e) {
            e.preventDefault();
            e.stopPropagation();
            setSelectedFromList(true);
            var _a = option.split(':'), hour = _a[0], minute = _a[1];
            onSelect({ hour: Number(hour), minute: Number(minute) });
        }, key: option }, option)); })));
    function getOptions() {
        var _a = checkIfStartAndEndLimitsAreValid(startLimit, endLimit), startIsValid = _a.startIsValid, endIsValid = _a.endIsValid;
        var start = startIsValid && startLimitString ? startLimitString : '00:00';
        var end = endIsValid && endLimitString ? endLimitString : '23:59';
        var endMilis = hourMinToMiliseconds(end);
        var options = [];
        var stop = false;
        for (var currentTime = start; hourMinToMiliseconds(currentTime) <= endMilis && !stop; currentTime = DateTime.fromMillis(hourMinToMiliseconds(currentTime) + step * 1000 * 60).toFormat('HH:mm')) {
            options.push(currentTime);
            // Force stop when it's the next day
            if (DateTime.fromMillis(hourMinToMiliseconds(currentTime) + step * 1000 * 60).toRelativeCalendar({
                locale: 'en',
            }) !== 'today' ||
                options.length > 99999) {
                stop = true;
            }
        }
        // Include selected option if not in there (in case it's not part of the steps)
        if (valueString && !options.includes(valueString)) {
            options.push(valueString);
        }
        return options.sort();
    }
};
var TimesListSelect$1 = React__default.memo(TimesListSelect);
function checkIfValueIsValid(value) {
    var valueIsValid = value && isValidHourMin(value);
    if (!valueIsValid && value) {
        console.error("Not a valid 'value' format: " + value + ", format should be 'HH:mm'");
    }
    return valueIsValid;
}
function checkIfStartAndEndLimitsAreValid(startLimit, endLimit) {
    var startIsValid = startLimit && isValidHourMin(startLimit);
    var endIsValid = endLimit && isValidHourMin(endLimit);
    var warningMessage = function (value) {
        return "Not a valid startLimit format: " + JSON.stringify(value) + ", format should be { hour: >= 0 && < 24, minute: >= 0 && < 60 }'";
    };
    if (!startIsValid && startLimit) {
        console.error(warningMessage(startLimit));
    }
    if (!endIsValid && endLimit) {
        console.error(warningMessage(endLimit));
    }
    return { startIsValid: startIsValid, endIsValid: endIsValid };
}
function isValidHourMin(_a) {
    var hour = _a.hour, minute = _a.minute;
    var hourIsValid = Number(hour) < 24 && Number(hour) >= 0 ? true : false;
    var minIsValid = Number(minute) < 60 && Number(minute) >= 0 ? true : false;
    return hourIsValid && minIsValid;
}
function hourMinToMiliseconds(hourMin) {
    return DateTime.fromFormat(hourMin, 'HH:mm', {}).valueOf();
}

var DateTimeDialog = function (props) {
    var _a = useState(function () { return getInitialDateValue(); }), selectedDate = _a[0], setSelectedDate = _a[1];
    var _b = useState(), month = _b[0], setMonth = _b[1];
    useUpdateEffect(function () {
        if (props.activeDateValue) {
            setSelectedDate(props.activeDateValue);
        }
    }, [props.activeDateValue]);
    var dateToShow = selectedDate || props.activeDateValue;
    // const jsDateToShow: Date | null = isoStringToJsDate(dateToShow)
    var jsDateToShow = dateToShow
        ? applyTimezoneOffsetToJsDate(DateTime.fromISO(dateToShow, { zone: 'UTC' }).toJSDate(), 'UTC')
        : undefined;
    var _c = useState('DATE'), showDateOrTime = _c[0], setShowDateOrTime = _c[1];
    var activateDatePicker = function () { return showDateOrTime !== 'DATE' && setShowDateOrTime('DATE'); };
    var activateTimePicker = function () { return showDateOrTime !== 'TIME' && !props.disableClock && setShowDateOrTime('TIME'); };
    return (React__default.createElement(Dialog, { title: props.dateSelectDialogTitle, showDialog: props.showDateDialog, type: "flexible", className: "date-time-dialog", onCloseDialog: props.closeDateDialog, hideHeader: props.hideDialogHeader, id: props.dialogId || 'teqplay-ui-dialog-date-time', footerContent: React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Button, { onClick: props.closeDateDialog }, "Cancel"),
            React__default.createElement(Button, { primary: true, onClick: onSave, disabled: !selectedDate }, "Ok")) },
        React__default.createElement("div", { className: "active-date-time" },
            React__default.createElement("span", { className: showDateOrTime === 'DATE' ? 'active' : '', onClick: activateDatePicker },
                jsDateToShow ? formatJsDatetoDateText(jsDateToShow, props.locale) : '',
                props.disableClock && jsDateToShow ? ' ' + formatJsDatetoTimeText(jsDateToShow, ':', true) : null),
            showDateOrTime === 'DATE' && !props.disableClock && (React__default.createElement("span", { onClick: activateTimePicker }, jsDateToShow ? formatJsDatetoTimeText(jsDateToShow, ' : ', true) : ''))),
        showDateOrTime === 'DATE' && jsDateToShow && props.showDateDialog && (React__default.createElement("div", { className: "padded-content date-picker-container" },
            React__default.createElement(DayPicker, { containerProps: { id: props.datePickerId || 'teqplay-ui-date-time-picker' }, onDayClick: onDateClick, initialMonth: jsDateToShow || undefined, month: month, locale: props.locale, modifiers: {
                    highlighted: jsDateToShow || undefined,
                }, captionElement: props.showMonthYearDropdown
                    ? function (_a) {
                        var date = _a.date, localeUtils = _a.localeUtils;
                        return (React__default.createElement(YearMonthDropdown, { date: date, localeUtils: localeUtils, onChange: function (date) { return setMonth(date); }, startYear: props.startYear, endYear: props.endYear }));
                    }
                    : undefined }),
            props.disableClock === true && (React__default.createElement(TimesListSelect$1, { value: jsDateToShow ? { hour: jsDateToShow.getHours(), minute: jsDateToShow.getMinutes() } : undefined, onSelect: onTimeSelect, step: props.timeListStep, startLimit: props.timeListStartLimit, endLimit: props.timeListEndLimit, id: props.timeListId })))),
        showDateOrTime === 'TIME' && props.disableClock !== true && (React__default.createElement(TimeKeeper, { onChange: onTimeSelect, time: jsDateToShow ? { hour: jsDateToShow.getHours(), minute: jsDateToShow.getMinutes() } : undefined, hour24Mode: true, switchToMinuteOnHourSelect: true }))));
    function getInitialDateValue() {
        // If a date is known don't fill the state, in the code it will use the prop
        // If it doens't have a date it should fill in a new Date object
        if (props.activeDateValue) {
            return props.activeDateValue;
        }
        if (props.useInitialStart) {
            return props.timeZone
                ? DateTime.local().setZone(props.timeZone).startOf(props.useInitialStart).toISO({ includeOffset: false })
                : DateTime.local().toLocal().startOf(props.useInitialStart).toISO({ includeOffset: false });
        }
        return props.timeZone
            ? DateTime.local().setZone(props.timeZone).toISO({ includeOffset: false })
            : DateTime.local().toLocal().toISO({ includeOffset: false });
    }
    function onDateClick(date) {
        var dateWithSetHourAndMinute = DateTime.fromObject({
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
            hour: jsDateToShow === null || jsDateToShow === void 0 ? void 0 : jsDateToShow.getHours(),
            minute: jsDateToShow === null || jsDateToShow === void 0 ? void 0 : jsDateToShow.getMinutes(),
            zone: 'UTC',
        });
        var newDate = dateWithSetHourAndMinute.toUTC().toISO();
        setSelectedDate(newDate);
        if (props.disableClock !== true) {
            // Go back to the input field
            setShowDateOrTime('TIME');
        }
    }
    function onTimeSelect(_a) {
        // In local/timezone time
        var hour = _a.hour, minute = _a.minute;
        var _b = DateTime.fromJSDate(jsDateToShow || new Date())
            .toUTC()
            .toObject(), year = _b.year, day = _b.day, month = _b.month;
        var dateWithNewHourAndMinute = DateTime.fromObject({
            year: year,
            month: month,
            day: day,
            hour: hour,
            minute: minute,
            zone: 'UTC',
        }).toISO();
        setSelectedDate(dateWithNewHourAndMinute);
    }
    function onSave() {
        if (selectedDate) {
            props.onDateTimeSave(DateTime.fromISO(selectedDate, { zone: 'UTC' }).toISO());
        }
        else {
            // Should never happen, beacause the save button should only be clickable when selected date is filled
            props.closeDateDialog();
        }
    }
};

var DateTimePicker = function (_a) {
    var _b = _a.dateSeperator, dateSeperator = _b === void 0 ? '/' : _b, _c = _a.timeSeperator, timeSeperator = _c === void 0 ? ':' : _c, onDateTimeChange = _a.onDateTimeChange, _d = _a.dialogTitle, dialogTitle = _d === void 0 ? 'Select date and time' : _d, locale = _a.locale, timeZone = _a.timeZone, icon = _a.icon, hideDialogHeader = _a.hideDialogHeader, disableClock = _a.disableClock, dialogId = _a.dialogId, datePickerId = _a.datePickerId, id = _a.id, useInitialStart = _a.useInitialStart, timeListId = _a.timeListId, timeListStep = _a.timeListStep, timeListStartLimit = _a.timeListStartLimit, timeListEndLimit = _a.timeListEndLimit, showMonthYearDropdown = _a.showMonthYearDropdown, startYear = _a.startYear, endYear = _a.endYear, props = __rest(_a, ["dateSeperator", "timeSeperator", "onDateTimeChange", "dialogTitle", "locale", "timeZone", "icon", "hideDialogHeader", "disableClock", "dialogId", "datePickerId", "id", "useInitialStart", "timeListId", "timeListStep", "timeListStartLimit", "timeListEndLimit", "showMonthYearDropdown", "startYear", "endYear"]);
    var format = "dd" + dateSeperator + "LL" + dateSeperator + "yyyy HH" + timeSeperator + "mm";
    // Value format: In timezone thats passed in || local timezone
    var _e = useState(''), value = _e[0], setValue = _e[1];
    var prevTimeZone = usePrevious(timeZone);
    var _f = useState(false), showDatePicker = _f[0], setShowDatePicker = _f[1];
    var datePickerRef = useRef(null);
    var ref = useRef(null);
    useEffect(function () {
        // default value, if initialDate is not defined, we use a empty string
        var newValue = '';
        if (props.value && props.value !== '') {
            if (DateTime.fromISO(props.value).isValid) {
                // the value supplied a valid isoDate so we parse that into the internal value format
                newValue = isoStringToFormat(props.value, format, timeZone);
            }
            else {
                // means we are dealing with a invalid iso string
                // so we can safely assume it's the format used by the inputmask
                newValue = props.value;
            }
        }
        if (value !== newValue) {
            setValue(newValue);
        }
    }, [props.value]);
    useUpdateEffect(function () {
        // Reset value back to iso based on prev timezon, than apply new timezone
        var isValid = isInputValidDate(value, prevTimeZone);
        var isoStringBasedOnTimezone = dateStringToIso(value, prevTimeZone);
        if (isValid && isoStringBasedOnTimezone) {
            setValue(isoStringToFormat(isoStringBasedOnTimezone, format, timeZone));
        }
    }, [timeZone]);
    useOnClickOutside(datePickerRef, function () {
        setShowDatePicker(false);
    });
    return (React__default.createElement("div", { className: "date-time-picker-container", ref: datePickerRef },
        React__default.createElement(FormFieldWrapper, { id: id || 'teqplay-ui-date-time-picker' },
            React__default.createElement(InputMask, __assign({}, props, { inputRef: function (newRef) { return (ref.current = newRef); }, mask: "99" + dateSeperator + "99" + dateSeperator + "9999 99" + timeSeperator + "99", value: value, onChange: onValueChange })),
            !props.disabled && (React__default.createElement("span", { className: "date-icon-container", onClick: function () { return setShowDatePicker(!showDatePicker); } }, icon))),
        React__default.createElement(DateTimeDialog, { onDateTimeSave: onDateSelect, showDateDialog: showDatePicker, closeDateDialog: function () { return setShowDatePicker(false); }, activeDateValue: value && isInputValidDate(value) ? dateStringToIso(value, 'UTC') || undefined : undefined, dateSelectDialogTitle: dialogTitle, locale: locale, timeSeperator: timeSeperator, timeZone: timeZone, hideDialogHeader: hideDialogHeader, disableClock: disableClock, dialogId: dialogId, datePickerId: datePickerId, useInitialStart: useInitialStart, 
            // when disabled clock, it's showing the time list
            timeListId: timeListId, timeListStep: timeListStep, timeListStartLimit: timeListStartLimit, timeListEndLimit: timeListEndLimit, showMonthYearDropdown: showMonthYearDropdown, startYear: startYear, endYear: endYear })));
    function onDateSelect(isoString) {
        // Get string value `dd/mm/yyyy hh:mm`
        var newInputValue = isoStringToFormat(isoString, format, 'UTC');
        // Hide date picker
        setShowDatePicker(false);
        // Set value inside input field
        setValue(newInputValue);
        // Send to parent component
        sendChangeToUser(newInputValue);
        // setTimeout(() => {
        //   if (disableClock === true) {
        //     ref.current?.setSelectionRange(11, 11)
        //     ref.current?.focus()
        //   }
        // })
    }
    function onValueChange(e) {
        // In given timezone || local time
        var inputValue = e.target.value;
        setValue(inputValue);
        sendChangeToUser(inputValue);
    }
    /**
     * Input value is in timezone/local time, so transfer it to UTC and send to user
     * @param inputValue
     */
    function sendChangeToUser(inputValue) {
        if (inputValue.length === 16 && !inputValue.includes('_')) {
            var isValid = isInputValidDate(inputValue, timeZone);
            // If valid, send date as utc iso string to user, otherwise send invalid input back
            onDateTimeChange(isValid ? dateStringToIso(inputValue, timeZone) : null, inputValue, isValid, props.name);
            return;
        }
        onDateTimeChange(null, inputValue, false, props.name);
    }
};

var TIMEZONE_NAMES = [
    'UTC',
    'Africa/Abidjan',
    'Africa/Accra',
    'Africa/Algiers',
    'Africa/Bissau',
    'Africa/Cairo',
    'Africa/Casablanca',
    'Africa/Ceuta',
    'Africa/El_Aaiun',
    'Africa/Johannesburg',
    'Africa/Juba',
    'Africa/Khartoum',
    'Africa/Lagos',
    'Africa/Maputo',
    'Africa/Monrovia',
    'Africa/Nairobi',
    'Africa/Ndjamena',
    'Africa/Sao_Tome',
    'Africa/Tripoli',
    'Africa/Tunis',
    'Africa/Windhoek',
    'America/Adak',
    'America/Anchorage',
    'America/Araguaina',
    'America/Argentina/Buenos_Aires',
    'America/Argentina/Catamarca',
    'America/Argentina/Cordoba',
    'America/Argentina/Jujuy',
    'America/Argentina/La_Rioja',
    'America/Argentina/Mendoza',
    'America/Argentina/Rio_Gallegos',
    'America/Argentina/Salta',
    'America/Argentina/San_Juan',
    'America/Argentina/San_Luis',
    'America/Argentina/Tucuman',
    'America/Argentina/Ushuaia',
    'America/Asuncion',
    'America/Atikokan',
    'America/Bahia',
    'America/Bahia_Banderas',
    'America/Barbados',
    'America/Belem',
    'America/Belize',
    'America/Blanc-Sablon',
    'America/Boa_Vista',
    'America/Bogota',
    'America/Boise',
    'America/Cambridge_Bay',
    'America/Campo_Grande',
    'America/Cancun',
    'America/Caracas',
    'America/Cayenne',
    'America/Chicago',
    'America/Chihuahua',
    'America/Costa_Rica',
    'America/Creston',
    'America/Cuiaba',
    'America/Curacao',
    'America/Danmarkshavn',
    'America/Dawson',
    'America/Dawson_Creek',
    'America/Denver',
    'America/Detroit',
    'America/Edmonton',
    'America/Eirunepe',
    'America/El_Salvador',
    'America/Fort_Nelson',
    'America/Fortaleza',
    'America/Glace_Bay',
    'America/Godthab',
    'America/Goose_Bay',
    'America/Grand_Turk',
    'America/Guatemala',
    'America/Guayaquil',
    'America/Guyana',
    'America/Halifax',
    'America/Havana',
    'America/Hermosillo',
    'America/Indiana/Indianapolis',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Tell_City',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Inuvik',
    'America/Iqaluit',
    'America/Jamaica',
    'America/Juneau',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/La_Paz',
    'America/Lima',
    'America/Los_Angeles',
    'America/Maceio',
    'America/Managua',
    'America/Manaus',
    'America/Martinique',
    'America/Matamoros',
    'America/Mazatlan',
    'America/Menominee',
    'America/Merida',
    'America/Metlakatla',
    'America/Mexico_City',
    'America/Miquelon',
    'America/Moncton',
    'America/Monterrey',
    'America/Montevideo',
    'America/Nassau',
    'America/New_York',
    'America/Nipigon',
    'America/Nome',
    'America/Noronha',
    'America/North_Dakota/Beulah',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/Ojinaga',
    'America/Panama',
    'America/Pangnirtung',
    'America/Paramaribo',
    'America/Phoenix',
    'America/Port-au-Prince',
    'America/Port_of_Spain',
    'America/Porto_Velho',
    'America/Puerto_Rico',
    'America/Punta_Arenas',
    'America/Rainy_River',
    'America/Rankin_Inlet',
    'America/Recife',
    'America/Regina',
    'America/Resolute',
    'America/Rio_Branco',
    'America/Santarem',
    'America/Santiago',
    'America/Santo_Domingo',
    'America/Sao_Paulo',
    'America/Scoresbysund',
    'America/Sitka',
    'America/St_Johns',
    'America/Swift_Current',
    'America/Tegucigalpa',
    'America/Thule',
    'America/Thunder_Bay',
    'America/Tijuana',
    'America/Toronto',
    'America/Vancouver',
    'America/Whitehorse',
    'America/Winnipeg',
    'America/Yakutat',
    'America/Yellowknife',
    'Antarctica/Casey',
    'Antarctica/Davis',
    'Antarctica/DumontDUrville',
    'Antarctica/Macquarie',
    'Antarctica/Mawson',
    'Antarctica/Palmer',
    'Antarctica/Rothera',
    'Antarctica/Syowa',
    'Antarctica/Troll',
    'Antarctica/Vostok',
    'Asia/Almaty',
    'Asia/Amman',
    'Asia/Anadyr',
    'Asia/Aqtau',
    'Asia/Aqtobe',
    'Asia/Ashgabat',
    'Asia/Atyrau',
    'Asia/Baghdad',
    'Asia/Baku',
    'Asia/Bangkok',
    'Asia/Barnaul',
    'Asia/Beirut',
    'Asia/Bishkek',
    'Asia/Brunei',
    'Asia/Chita',
    'Asia/Choibalsan',
    'Asia/Colombo',
    'Asia/Damascus',
    'Asia/Dhaka',
    'Asia/Dili',
    'Asia/Dubai',
    'Asia/Dushanbe',
    'Asia/Famagusta',
    'Asia/Gaza',
    'Asia/Hebron',
    'Asia/Ho_Chi_Minh',
    'Asia/Hong_Kong',
    'Asia/Hovd',
    'Asia/Irkutsk',
    'Asia/Jakarta',
    'Asia/Jayapura',
    'Asia/Jerusalem',
    'Asia/Kabul',
    'Asia/Kamchatka',
    'Asia/Karachi',
    'Asia/Kathmandu',
    'Asia/Khandyga',
    'Asia/Kolkata',
    'Asia/Krasnoyarsk',
    'Asia/Kuala_Lumpur',
    'Asia/Kuching',
    'Asia/Macau',
    'Asia/Magadan',
    'Asia/Makassar',
    'Asia/Manila',
    'Asia/Nicosia',
    'Asia/Novokuznetsk',
    'Asia/Novosibirsk',
    'Asia/Omsk',
    'Asia/Oral',
    'Asia/Pontianak',
    'Asia/Pyongyang',
    'Asia/Qatar',
    'Asia/Qostanay',
    'Asia/Qyzylorda',
    'Asia/Riyadh',
    'Asia/Sakhalin',
    'Asia/Samarkand',
    'Asia/Seoul',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Srednekolymsk',
    'Asia/Taipei',
    'Asia/Tashkent',
    'Asia/Tbilisi',
    'Asia/Tehran',
    'Asia/Thimphu',
    'Asia/Tokyo',
    'Asia/Tomsk',
    'Asia/Ulaanbaatar',
    'Asia/Urumqi',
    'Asia/Ust-Nera',
    'Asia/Vladivostok',
    'Asia/Yakutsk',
    'Asia/Yangon',
    'Asia/Yekaterinburg',
    'Asia/Yerevan',
    'Atlantic/Azores',
    'Atlantic/Bermuda',
    'Atlantic/Canary',
    'Atlantic/Cape_Verde',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Atlantic/Reykjavik',
    'Atlantic/South_Georgia',
    'Atlantic/Stanley',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Broken_Hill',
    'Australia/Currie',
    'Australia/Darwin',
    'Australia/Eucla',
    'Australia/Hobart',
    'Australia/Lindeman',
    'Australia/Lord_Howe',
    'Australia/Melbourne',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/Amsterdam',
    'Europe/Andorra',
    'Europe/Astrakhan',
    'Europe/Athens',
    'Europe/Belgrade',
    'Europe/Berlin',
    'Europe/Brussels',
    'Europe/Bucharest',
    'Europe/Budapest',
    'Europe/Chisinau',
    'Europe/Copenhagen',
    'Europe/Dublin',
    'Europe/Gibraltar',
    'Europe/Helsinki',
    'Europe/Istanbul',
    'Europe/Kaliningrad',
    'Europe/Kiev',
    'Europe/Kirov',
    'Europe/Lisbon',
    'Europe/London',
    'Europe/Luxembourg',
    'Europe/Madrid',
    'Europe/Malta',
    'Europe/Minsk',
    'Europe/Monaco',
    'Europe/Moscow',
    'Europe/Oslo',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Riga',
    'Europe/Rome',
    'Europe/Samara',
    'Europe/Saratov',
    'Europe/Simferopol',
    'Europe/Sofia',
    'Europe/Stockholm',
    'Europe/Tallinn',
    'Europe/Tirane',
    'Europe/Ulyanovsk',
    'Europe/Uzhgorod',
    'Europe/Vienna',
    'Europe/Vilnius',
    'Europe/Volgograd',
    'Europe/Warsaw',
    'Europe/Zaporozhye',
    'Europe/Zurich',
    'Indian/Chagos',
    'Indian/Christmas',
    'Indian/Cocos',
    'Indian/Kerguelen',
    'Indian/Mahe',
    'Indian/Maldives',
    'Indian/Mauritius',
    'Indian/Reunion',
    'Pacific/Apia',
    'Pacific/Auckland',
    'Pacific/Bougainville',
    'Pacific/Chatham',
    'Pacific/Chuuk',
    'Pacific/Easter',
    'Pacific/Efate',
    'Pacific/Enderbury',
    'Pacific/Fakaofo',
    'Pacific/Fiji',
    'Pacific/Funafuti',
    'Pacific/Galapagos',
    'Pacific/Gambier',
    'Pacific/Guadalcanal',
    'Pacific/Guam',
    'Pacific/Honolulu',
    'Pacific/Kiritimati',
    'Pacific/Kosrae',
    'Pacific/Kwajalein',
    'Pacific/Majuro',
    'Pacific/Marquesas',
    'Pacific/Nauru',
    'Pacific/Niue',
    'Pacific/Norfolk',
    'Pacific/Noumea',
    'Pacific/Pago_Pago',
    'Pacific/Palau',
    'Pacific/Pitcairn',
    'Pacific/Pohnpei',
    'Pacific/Port_Moresby',
    'Pacific/Rarotonga',
    'Pacific/Tahiti',
    'Pacific/Tarawa',
    'Pacific/Tongatapu',
    'Pacific/Wake',
    'Pacific/Wallis',
];

var DateRangeDialog = function (props) {
    // internal state of isoStrings for the selected range
    var _a = useState(null), fromDate = _a[0], setFromDate = _a[1];
    var _b = useState(null), toDate = _b[0], setToDate = _b[1];
    var _c = useState(null), hoveringTo = _c[0], setHoveringTo = _c[1];
    useEffect(function () {
        if (props.activeFromValue) {
            setFromDate(props.activeFromValue);
        }
        else {
            setFromDate(null);
        }
    }, [props.activeFromValue]);
    useEffect(function () {
        if (props.activeToValue) {
            setToDate(props.activeToValue);
        }
        else {
            setToDate(null);
        }
    }, [props.activeToValue]);
    // parse iso strings back to a js date to use in the Daypicker component
    var fromDateToShow = fromDate ? convertStringToJsDate(fromDate) : undefined;
    var toDateToShow = toDate ? convertStringToJsDate(toDate) : undefined;
    // daypicker is a bit particular in the way it wants to receive the date range
    var _d = { from: fromDateToShow, to: toDateToShow }, from = _d.from, to = _d.to;
    var disabledDays = to ? undefined : { before: from };
    var modifiers = { start: fromDateToShow, end: toDateToShow };
    // number of month calendars being rendered by the datepicker
    var numberOfMonthsToShow = props.numberOfMonths || 2;
    return (React__default.createElement(Dialog, { title: props.dateSelectDialogTitle, showDialog: props.showDateDialog, type: "flexible", onCloseDialog: props.closeDateDialog, hideHeader: props.hideDialogHeader, id: props.dialogId || 'teqplay-ui-dialog-date-range', footerContent: React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Button, { onClick: props.closeDateDialog }, "Cancel"),
            React__default.createElement(Button, { primary: true, onClick: onSave, disabled: !fromDate || !toDate }, "Ok")) },
        React__default.createElement("div", { className: "active-date-time" },
            React__default.createElement("span", { className: "active" }, "" + (fromDateToShow ? formatJsDatetoDateText(fromDateToShow, props.locale) : '') + (toDateToShow ? " - " + formatJsDatetoDateText(toDateToShow, props.locale) : ''))),
        React__default.createElement("div", { className: "padded-content date-picker-container" },
            React__default.createElement(DayPicker, { containerProps: { id: props.datePickerId || 'teqplay-ui-date-range-picker' }, className: "range-picker Range", numberOfMonths: numberOfMonthsToShow, 
                // any is used here to prevent typescript from moading about undefined dates
                // this is incorrectly typed in the lib, dates can be undefined.
                selectedDays: [from, { from: from, to: hoveringTo }], 
                // selectedDays={{ from, to } as any}
                modifiers: modifiers, onDayClick: onDateClick, disabledDays: disabledDays, onDayMouseEnter: handleDayMouseEnter, locale: props.locale, month: from, captionElement: props.showMonthYearDropdown
                    ? function (_a) {
                        var date = _a.date, localeUtils = _a.localeUtils;
                        return (React__default.createElement(YearMonthDropdown, { date: date, localeUtils: localeUtils, onChange: function (date) { return setFromDate(date.toISOString()); }, startYear: props.startYear, endYear: props.endYear }));
                    }
                    : undefined }))));
    function handleDayMouseEnter(day) {
        if (!isSelectingFirstDay(day, from, to)) {
            setHoveringTo(day);
        }
    }
    function convertStringToJsDate(date) {
        if (date) {
            return DateTime.fromISO(date).toUTC().toJSDate();
        }
        else {
            return undefined;
        }
    }
    function onDateClick(date) {
        if (from && to) {
            // From and to already known, so reset and set the from date
            setFromDate(DateTime.fromJSDate(date).toUTC().toISO());
            setToDate(null);
            setHoveringTo(null);
            return;
        }
        if (!from && !to) {
            // No date set yet, so set from date
            setFromDate(DateTime.fromJSDate(date).toUTC().toISO());
            setHoveringTo(null);
        }
        else if (from && date.valueOf() > new Date(from).valueOf()) {
            // From date is already selected, so set to date
            setToDate(DateTime.fromJSDate(date).toUTC().toISO());
            setHoveringTo(date);
        }
    }
    function onSave() {
        if (fromDate && toDate) {
            props.onDateSave(fromDate, toDate);
        }
        else {
            // Should never happen
            props.closeDateDialog();
        }
    }
};
function isSelectingFirstDay(date, from, to) {
    var isBeforeFirstDay = from && DateUtils.isDayBefore(date, from);
    var isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
}

var DateRangePicker = function (_a) {
    var _b = _a.dateSeperator, dateSeperator = _b === void 0 ? '/' : _b, onDateChange = _a.onDateChange, _c = _a.dialogTitle, dialogTitle = _c === void 0 ? 'Select Date' : _c, locale = _a.locale, timeZone = _a.timeZone, icon = _a.icon, fromValue = _a.fromValue, toValue = _a.toValue, hideDialogHeader = _a.hideDialogHeader, dialogId = _a.dialogId, datePickerId = _a.datePickerId, fromInputId = _a.fromInputId, toInputId = _a.toInputId, id = _a.id, showMonthYearDropdown = _a.showMonthYearDropdown, startYear = _a.startYear, endYear = _a.endYear, props = __rest(_a, ["dateSeperator", "onDateChange", "dialogTitle", "locale", "timeZone", "icon", "fromValue", "toValue", "hideDialogHeader", "dialogId", "datePickerId", "fromInputId", "toInputId", "id", "showMonthYearDropdown", "startYear", "endYear"]);
    var format = "dd" + dateSeperator + "LL" + dateSeperator + "yyyy HH:mm";
    var prevTimeZone = usePrevious(timeZone);
    var _d = useState(''), fromDateValue = _d[0], setFromDateValue = _d[1];
    var _e = useState(''), toDateValue = _e[0], setToDateValue = _e[1];
    useEffect(function () {
        var newValue = handleValueChange(fromValue);
        if (fromValue !== newValue) {
            setFromDateValue(newValue);
        }
    }, [fromValue]);
    useEffect(function () {
        var newValue = handleValueChange(toValue);
        if (toValue !== newValue) {
            setToDateValue(newValue);
        }
    }, [toValue]);
    // deal with timezone changes and convert the date values accordingly.
    useUpdateEffect(function () {
        // Reset value back to iso based on prev timezon, than apply new timezone
        var isFromValid = isInputValidDate(fromDateValue, prevTimeZone);
        var isToValid = isInputValidDate(toDateValue, prevTimeZone);
        var isoStringBasedOnTimezoneFrom = dateStringToIso(fromDateValue, prevTimeZone);
        var isoStringBasedOnTimezoneTo = dateStringToIso(toDateValue, prevTimeZone);
        if (isFromValid && isoStringBasedOnTimezoneFrom) {
            setFromDateValue(isoStringToFormat(isoStringBasedOnTimezoneFrom, format, timeZone));
        }
        if (isToValid && isoStringBasedOnTimezoneTo) {
            setToDateValue(isoStringToFormat(isoStringBasedOnTimezoneTo, format, timeZone));
        }
    }, [timeZone]);
    var _f = useState(false), showDatePicker = _f[0], setShowDatePicker = _f[1];
    var datePickerRef = useRef(null);
    useOnClickOutside(datePickerRef, function () {
        setShowDatePicker(false);
    });
    return (React__default.createElement("div", { className: "date-time-picker-container range-picker", ref: datePickerRef },
        React__default.createElement(FormFieldWrapper, { id: id || 'teqplay-ui-date-range-picker' },
            React__default.createElement(InputMask, __assign({}, props, { mask: "99" + dateSeperator + "99" + dateSeperator + "9999", value: fromDateValue, className: "from-picker", id: fromInputId || 'teqplay-ui-daterange-from-input', onChange: function (e) {
                    onValueChange(e, 'FROM');
                } })),
            !props.disabled && (React__default.createElement("span", { className: "date-icon-container", onClick: function () { return setShowDatePicker(!showDatePicker); } }, icon))),
        React__default.createElement(FormFieldWrapper, null,
            React__default.createElement(InputMask, __assign({}, props, { mask: "99" + dateSeperator + "99" + dateSeperator + "9999", value: toDateValue, className: "to-picker", id: toInputId || 'teqplay-ui-daterange-to-input', onChange: function (e) {
                    onValueChange(e, 'TO');
                } })),
            !props.disabled && (React__default.createElement("span", { className: "date-icon-container", onClick: function () { return setShowDatePicker(!showDatePicker); } }, icon))),
        React__default.createElement(DateRangeDialog, { onDateSave: onDateSelect, showDateDialog: showDatePicker, closeDateDialog: function () { return setShowDatePicker(false); }, activeFromValue: isInputValidDate(fromDateValue) ? dateStringToIso(fromDateValue) || undefined : undefined, activeToValue: isInputValidDate(toDateValue) ? dateStringToIso(toDateValue) || undefined : undefined, dateSelectDialogTitle: dialogTitle, locale: locale, hideDialogHeader: hideDialogHeader, dialogId: dialogId, datePickerId: datePickerId, showMonthYearDropdown: showMonthYearDropdown, startYear: startYear, endYear: endYear })));
    function onDateSelect(from, to) {
        // Get string value dd/mm/yyyy
        var newFromInputValue = isoStringToFormat(from, format);
        var newToInputValue = isoStringToFormat(to, format);
        // Hide date picker
        setShowDatePicker(false);
        // Set value inside input field
        setFromDateValue(newFromInputValue);
        setToDateValue(newToInputValue);
        // prepare values for parent component
        /**
         * Function to modify the selected date's hour.
         * If the @param is true, it sets the hours to 00:00:00. If the @param is false it sets the hours to 23:59:59.
         * Then converts the selected DateTime to UTC and ISO.
         *
         * @param {boolean} startTime   Determines the hours to modify.
         *
         * */
        function formatDateObject(startTime) {
            var date = DateTime.fromISO(startTime ? from : to, { zone: timeZone }).toObject();
            return DateTime.fromObject({
                year: date.year,
                month: date.month,
                day: date.day,
                hour: startTime ? 0 : 23,
                minute: startTime ? 0 : 59,
                second: startTime ? 0 : 59,
                zone: timeZone,
            })
                .toUTC()
                .toISO();
        }
        // Send to parent component
        onDateChange({ isoString: formatDateObject(true), inputValue: newFromInputValue, isValid: true }, { isoString: formatDateObject(false), inputValue: newToInputValue, isValid: true }, props.name);
    }
    function onValueChange(e, field) {
        var inputValue = e.target.value;
        if (field === 'FROM') {
            setFromDateValue(inputValue);
        }
        else {
            setToDateValue(inputValue);
        }
        // if the fromDate is filled in, the opposite value is the toValue
        // we use this to send to the user when either of the fields has changed.
        // This way to get an update on both fields.
        var oppositeValue = field === 'FROM' ? toDateValue : fromDateValue;
        var oppositeFieldValues = {
            isoString: oppositeValue && isInputValidDate(oppositeValue)
                ? DateTime.fromObject({
                    minute: 0,
                    hour: 0,
                    day: parseInt(oppositeValue.slice(0, 2)),
                    month: parseInt(oppositeValue.slice(3, 5)),
                    year: parseInt(oppositeValue.slice(6, 10)),
                    zone: timeZone,
                })
                    .toUTC()
                    .toISO()
                : null,
            inputValue: oppositeValue,
            isValid: isInputValidDate(oppositeValue),
        };
        if (inputValue.length === 10 && !inputValue.includes('_')) {
            var newValues = {
                isoString: isInputValidDate(inputValue)
                    ? DateTime.fromObject({
                        minute: 0,
                        hour: 0,
                        day: parseInt(inputValue.slice(0, 2)),
                        month: parseInt(inputValue.slice(3, 5)),
                        year: parseInt(inputValue.slice(6, 10)),
                        zone: timeZone,
                    })
                        .toUTC()
                        .toISO()
                    : null,
                inputValue: inputValue,
                isValid: isInputValidDate(inputValue),
            };
            onDateChange(field === 'FROM' ? newValues : oppositeFieldValues, field === 'TO' ? newValues : oppositeFieldValues, props.name);
            return;
        }
        var invalidValues = {
            isoString: null,
            inputValue: inputValue,
            isValid: false,
        };
        onDateChange(field === 'FROM' ? invalidValues : oppositeFieldValues, field === 'TO' ? invalidValues : oppositeFieldValues, props.name);
    }
    function handleValueChange(updatedValue) {
        var newValue = '';
        if (updatedValue && updatedValue !== '') {
            if (DateTime.fromISO(updatedValue).isValid) {
                // the value supplied a valid isoDate so we parse that into the internal value format
                newValue = isoStringToFormat(updatedValue, format, timeZone);
            }
            else {
                // means we are dealing with a invalid iso string
                // so we can safely assume it's the format used by the inputmask
                newValue = updatedValue;
            }
        }
        return newValue;
    }
};

var NumberInput = function (_a) {
    var onChangeFormValue = _a.onChangeFormValue, showClearButton = _a.showClearButton, onClear = _a.onClear, inputIcon = _a.inputIcon, maxDecimals = _a.maxDecimals, _b = _a.onlyRenderInput, onlyRenderInput = _b === void 0 ? false : _b, _c = _a.useComma, useComma = _c === void 0 ? false : _c, props = __rest(_a, ["onChangeFormValue", "showClearButton", "onClear", "inputIcon", "maxDecimals", "onlyRenderInput", "useComma"]);
    var inputRender = (React__default.createElement("input", __assign({ type: "tel" // we use tel, this makes sure the input behaves like a type of text, but shows a numeric keyboard on mobile devices.
     }, props, { onKeyUp: function (event) {
            // prevents the onChange from triggering if a invalid input is being pressed.
            if (!isValidNumberFieldInput(event)) {
                event.preventDefault();
            }
        }, onChange: onChange })));
    if (onlyRenderInput) {
        return inputRender;
    }
    return (React__default.createElement(FormFieldWrapper, { className: inputIcon ? 'field-has-icon' : '' },
        inputIcon && React__default.createElement("div", { className: "input-icon" }, inputIcon),
        inputRender,
        showClearButton === true && (React__default.createElement(Button, { className: "clear-button", iconButton: true, onClick: handleClear },
            React__default.createElement("i", { className: "icon-cancel-1" })))));
    /* This function can be in a common location like libs or utils... TODO: we can move this kind of functions to utils library */
    function isNumeric(str) {
        return (!isNaN(str) && // use type coercion (`parseFloat` alone does not do this)...
            !isNaN(parseFloat(str))); // ...and ensure strings of whitespace fail
    }
    function onChange(e) {
        var value = e.target.value;
        // replaces the , with a dot to keep things consistent and in line with javascript numbers
        var newValue = value.replace(/,/g, '.'); // 9.,
        // @ts-ignore -- Should just be able to check a string with isNaN, crazy TS
        var isInvalidNumberString = isNumeric(newValue);
        // if the value is invalid, for example someone copy pastes a string value in the form, we just ignore it and return a empty value
        if (!isInvalidNumberString) {
            newValue = newValue
                .replace(/[^.0-9]/g, '') // clear everythhing except . and numbers if the string has invalid number input
                .replace(newValue.substring(newValue.length - 1), '') // if user enter second time to . or , isNumeric function returns false than
                .replace(useComma ? '.' : ',', useComma ? ',' : '.'); // after clear the string, replace . and , with given useComma property
            onChangeFormValue(props.name || '', newValue);
        }
        else {
            var inputValue = maxDecimals && maxDecimals !== 0 // 0 is falsey value in javascript
                ? createNumberWithSetDecimals(value.replace(useComma ? '.' : ',', useComma ? ',' : '.'), maxDecimals)
                : newValue.replace('.', '');
            onChangeFormValue(props.name || '', inputValue);
        }
    }
    function handleClear() {
        if (onClear) {
            onClear();
        }
    }
    function createNumberWithSetDecimals(stringValue, maxFractionDigits) {
        var fractionDigits = stringValue.split(useComma ? ',' : '.')[1];
        // Break out in case there are no FractionDigits found //
        if (!fractionDigits)
            return stringValue;
        var integerDigits = stringValue.split(useComma ? ',' : '.')[0];
        var newFractionDigits = fractionDigits.substring(0, maxFractionDigits);
        return "" + integerDigits + (useComma ? ',' : '.') + newFractionDigits;
    }
    function isValidNumberFieldInput(event) {
        var keyCode = event.which || event.keyCode;
        var isNumeric = 
        // checks keyboard keys 0 to 9
        (keyCode >= 48 && keyCode <= 57) ||
            // checks numpad keys 0 to 9
            (keyCode >= 96 && keyCode <= 105);
        // checks all the modifiers and backspace ( code 8)
        var isModifier = event.altKey || event.ctrlKey || event.shiftKey || keyCode === 8;
        // checks 188 ( comma ,) and 190 ( period .)
        var isSeparator = keyCode === 188 || keyCode === 190;
        // checks the keyboard arrow keys
        var isArrowKey = keyCode >= 37 && keyCode <= 40;
        // Checks the tab (code 9) key and enter (code 13) press
        var isFormActionKey = keyCode === 9 || keyCode === 13;
        return isNumeric || isModifier || isSeparator || isArrowKey || isFormActionKey;
    }
};

var Switch = function (_a) {
    var className = _a.className, field = _a.field, label = _a.label, checked = _a.checked, labelClassName = _a.labelClassName, strikeThrough = _a.strikeThrough, _b = _a.switchSize, switchSize = _b === void 0 ? 'large' : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, props = __rest(_a, ["className", "field", "label", "checked", "labelClassName", "strikeThrough", "switchSize", "disabled"]);
    return (createElement("label", { className: "switch " + (className || '') },
        createElement("div", { className: "custom-switch " + (switchSize === 'large' ? 'large' : 'small') },
            createElement("input", __assign({ className: "input " + (disabled ? ' disabled' : ''), type: 'checkbox', name: field, id: field }, props, { checked: checked })),
            createElement("span", { className: "slider round" })),
        label ? (createElement("label", { className: "form-field-label " + (strikeThrough ? 'strike-through-label' : '') + " label " + (label ? '' : 'label-hidden') + " " + (disabled ? 'disabled' : '') + " " + (labelClassName || '') + " " + (switchSize === 'large' ? 'large' : 'small'), htmlFor: field }, label)) : ('')));
};

export default Button;
export { Button, ButtonGroup, Checkbox, Collapse, DATE_TIME_FORMAT, DateDialog, DatePicker, DateRangePicker, DateTimeDialog, DateTimePicker, Dialog, DropDownButton, DropDownButtonItem, FormElement, FormErrorMessage, FormFieldButtonWrapper, FormFieldWrapper, FormLabel, FormWrapper, InlineFormElement, MultiSelectOption, MultiSelectionBox, NavigationButton, NumberInput, SelectionBox, Switch, TIMEZONE_NAMES, Tabs, TextArea, TextField$1 as TextField, TimePicker$1 as TimePicker, applyTimezoneOffsetToJsDate, calculateMaxMonthDate, calculateMinMonthDate, dateStringToDateObject, dateStringToIso, dateStringToZonedJSDate, datesAreEqual, formatDate, formatDateTime, formatDateWithoutYear, formatJSDateWithZone, formatJsDatetoDateText, formatJsDatetoTimeText, formatTime, getMonthAsNumber, isAfter, isBefore, isExactSameDay, isInTheFuture, isInThePast, isInputValidDate, isSameDay, isSameDayAndMonth, isSameMonth, isSameYear, isTimeInputValid, isToday, isValidDate, isValidDateTime, isoStringToFormat, parseDateOrStringToDateTime, parseDateOrStringToFormat, snapToHour };
